import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type ServerEnvs = {
  ENV: 'qa' | 'titus' | '';
  ENV_PREFIX: string;
  MIXPANEL_TOKEN: string;
  NEW_RELIC_BROWSER_APP_ID: string;
  TAG: string;
};

export const defaultServerEnvs: ServerEnvs = {
  ENV: '',
  ENV_PREFIX: '',
  MIXPANEL_TOKEN: '',
  NEW_RELIC_BROWSER_APP_ID: '',
  TAG: '',
};

export const CONFIG_QUERY_PARAM_ALLOWED_HOSTNAME_ENDS_WITH = 'optibus.co';

interface ParamsObject {
  customer: string | null;
  referrerURL?: URL;
  configURL?: URL;
  activeConfigurationsURL?: URL;
  logoURL: string;
  backgroundURL?: string;
  validateSessionWithJWT: { enabled: boolean };
  clientId: string | null;
  isAppReady: boolean;
  envData: ServerEnvs;
}

const defaultParams = {
  customer: null,
  logoURL: '/Logo.svg',
  isAppReady: false,
  envData: defaultServerEnvs,
  validateSessionWithJWT: { enabled: false },
  clientId: null,
};

export const useQueryParam = (name: string): string | null =>
  useMemo(() => new URLSearchParams(window.location.search).get(name), [name]);

export const AppContext = React.createContext<ParamsObject>(defaultParams);
/* just for local testing
const mockEnvCall = () => new Promise((resolve) => {
  setTimeout(() => {
    resolve({
      data: {
        ENV: 'qa',
        ENV_PREFIX: '',
        MIXPANEL_TOKEN: '',
        NEW_RELIC_BROWSER_APP_ID: '',
        TAG: ''
      }
    });
  }, 2000);
});
*/

const getEnvData = async (
  url: string,
  onSuccess: React.Dispatch<React.SetStateAction<ServerEnvs>>
) => {
  try {
    const response = await axios.get(`${url}/environment/`);
    // const response = await mockEnvCall(); for local testing
    if (response && response.data) {
      onSuccess(response.data);
    }
  } catch (e) {
    console.error(e);
  }
};

export const AppContextProvider: React.FC = ({ children }) => {
  const [backgroundURL, setBackgroundURL] = useState<string>();
  const [validateSessionWithJWTEnabled, setValidateSessionWithJWTEnabled] =
    useState<boolean>(false);
  const [logoURL, setLogoURL] = useState('/Logo.svg');
  const customer = useQueryParam('customer');
  const referrerQueryParam = useQueryParam('referrer');
  const configQueryParam = useQueryParam('config');
  const clientId = useQueryParam('clientId');
  const oldPath = useQueryParam('oldPath');
  const [isAppReady, setIsAppReady] = useState(false);
  const { i18n } = useTranslation();
  const [envData, setEnvData] = useState(defaultServerEnvs);

  const referrerParam = referrerQueryParam ?? process.env.REACT_APP_REFERRER ?? null;

  const referrerURL = useMemo<URL | undefined>(() => {
    const prevReferrerURL = window.localStorage.getItem('referrerURL');
    let url;
    try {
      if (referrerParam !== null) {
        url = new URL(`${window.location.protocol}//${referrerParam}${oldPath || ''}`);
        window.localStorage.setItem('referrerURL', referrerParam);
      } else if (prevReferrerURL !== null) {
        url = new URL(`${window.location.protocol}//${prevReferrerURL}${oldPath || ''}`);
      }
      if (url && customer) {
        url.searchParams.append('customer', customer);
      }
    } catch (e) {
      // An invalid referrer was given, ignore it and continue with undefined url
    }

    return url;
  }, [referrerParam, oldPath, customer]);

  const configURL = useMemo<URL | undefined>(() => {
    let url = undefined;
    try {
      if (configQueryParam) {
        url = new URL(`${window.location.protocol}//${configQueryParam}`);
        if (!url.hostname.endsWith(CONFIG_QUERY_PARAM_ALLOWED_HOSTNAME_ENDS_WITH)) {
          url = undefined;
        }
        if (url && customer) {
          url.searchParams.append('customer', customer);
        }
      }
    } catch (e) {
      // An invalid configQueryParam was given, ignore it and continue with undefined url
      return undefined;
    }
    return url;
  }, [configQueryParam, customer]);

  const activeConfigurationsURL = configURL || referrerURL;

  useEffect(() => {
    if (!clientId && referrerURL) {
      console.error('clientId is missing');
      window.location.assign(referrerURL!.href);
    }
  }, [clientId, referrerURL]);

  useEffect(() => {
    if (activeConfigurationsURL) {
      getEnvData(activeConfigurationsURL.origin, setEnvData);
    }
  }, [activeConfigurationsURL]);

  // get logo and background
  useEffect(() => {
    const getConfig = async () => {
      try {
        const config = await axios.get(
          `${activeConfigurationsURL!.origin}/configurations/company`,
          {
            headers: {
              ...(customer && { customer }),
            },
          }
        );
        setBackgroundURL(config.data?.configuration?.background);
        config.data?.configuration?.logo && setLogoURL(config.data.configuration.logo);
        config.data?.configuration?.language
          ? i18n.changeLanguage(config.data.configuration.language, () => setIsAppReady(true))
          : setIsAppReady(true);
        config?.data?.configuration?.features?.gradual?.validateSessionWithJWT?.enabled &&
          setValidateSessionWithJWTEnabled(true);
      } catch {
        // If the request failes, the app should stop waiting for the customer's info and load without it
        setIsAppReady(true);
      }
    };
    if (activeConfigurationsURL && !isAppReady) {
      getConfig();
    }
  }, [customer, activeConfigurationsURL, i18n, isAppReady]);

  const value = useMemo(
    () => ({
      customer,
      referrerURL,
      configURL,
      activeConfigurationsURL,
      backgroundURL,
      logoURL,
      isAppReady,
      envData,
      validateSessionWithJWT: { enabled: validateSessionWithJWTEnabled },
      clientId,
    }),
    [
      customer,
      referrerURL,
      configURL,
      activeConfigurationsURL,
      backgroundURL,
      logoURL,
      isAppReady,
      envData,
      validateSessionWithJWTEnabled,
      clientId,
    ]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
